/**
 * @version 2.0
 * @author Kelnik Studios {http://kelnik.ru}
 * @link https://gitbook.kelnik.ru/main/front-end/components/popup.html documentation
 */

/**
 * DEPENDENCIES
 */
import 'slick-carousel/slick/slick.css';
import {InternetConnection, Utils} from 'common/scripts/utils';
import basicTemplate from './templates/basic-popup.twig';
import fullTemplate from './templates/full-popup.twig';
import imageTemplate from './templates/image-popup.twig';
import {MOBILE_BREAKPOINT} from 'common/scripts/constants';
import Observer from 'common/scripts/observer';
import slick from 'slick-carousel'; // eslint-disable-line
import sliderTemplate from './templates/slider-popup.twig';
import TouchZoom from 'components/touch-zoom/touch-zoom';

const observer = new Observer();
const emptyFunction = () => {
    return null;
};

export class AbstractPopup {
    constructor(options = {}) {
        this.openStateClass = options.openStateClass || 'popup_state_open';
        this.closeButtonAriaLabel = options.closeButtonAriaLabel || 'Закрыть всплывающее окно';
        this.openBtns = options.openButtons;
        this.currentState = false;
        this.onCreate = options.onCreate || emptyFunction;
        this.onClose = options.onClose || emptyFunction;

        /* Определяется в методе _getElements() */
        this.popup = null;
        this.content = null;
        this.closeBtns = null;

        /* Определяется в методе _setTemplates() */
        this.template = null;

        /* Определяется в методе _setData() */
        this.data = null;

        this._bindMethodsContext();
    }

    /**
     * @Public
     * Входная точка проекта
     *
     */
    init() {
        this._bindOpenEvents();
    }

    /**
     * @Public
     * Отображение попапа на экране
     *
     */
    showPopup() {
        this.popup.classList.add(this.openStateClass);

        Utils.bodyFixed(this.popup);
    }

    /**
     * @Public
     * Открытие попапа
     *
     */
    displayPopup() {
        this.showPopup();

        observer.publish('popup:open', this);

        this.currentState = true;
    }

    /**
     * @Public
     * Сокрытие попапа с дисплея
     *
     */
    hidePopup() {
        this.popup.classList.remove(this.openStateClass);

        Utils.bodyStatic();
    }

    /**
     * @Public
     * Удаление попапа из DOM-дерева
     *
     */
    removePopup() {
        this.hidePopup();
        Utils.removeElement(this.popup);

        observer.publish('popup:close', this);

        this.currentState = false;

        document.removeEventListener('keyup', this._closeByKeyboard);
        document.removeEventListener('click', this._closeByMouse);

        this.onClose();
    }

    /**
     * @Public
     * Перезапуск попапа
     *
     */
    reOpen() {
        this.removePopup();
        this._open();
    }

    /**
     * @Private
     * Бинд контекста для методов класса
     *
     */
    _bindMethodsContext() {
        this._closeByKeyboard = this._closeByKeyboard.bind(this);
        this.removePopup = this.removePopup.bind(this);
    }

    /**
     * @Private
     * Установка шаблона попапа
     *
     */
    _setTemplates() {
        this.template = basicTemplate;
    }

    /**
     * @Abstract Private
     * Установка данных в попапе
     *
     */
    _setData() {
        throw new Error(`Method _setData() must be overridden`);
    }

    /**
     * @Private
     * Вставка ноды попапа в DOM-дерево
     *
     */
    _setPopupOnPage() {
        document.body.insertAdjacentHTML('afterbegin', this.template(this.data));
    }

    /**
     * @Private
     * Получение нод DOM-дерева из попапа
     *
     */
    _getElements() {
        this.popup = document.querySelector('.j-popup__body');
        this.content = document.querySelector('.j-popup__content');

        const closeBtn = this.popup.querySelectorAll('.j-popup__close');
        const overLay = this.popup.querySelector('.j-popup__overlay');

        this.closeBtns = [...closeBtn, overLay];
    }

    /**
     * @Private
     * Генерация попапа
     *
     */
    _createPopup() {
        this._setData();
        this._setTemplates();
        this._setPopupOnPage();
        this._getElements();
        this._bindCloseEvents();

        this.onCreate();
    }

    /**
     * @Abstract Private
     * Коллбэк для события открытия
     *
     */
    _open() {
        throw new Error(`Method _open() must be overridden`);
    }

    /**
     * @Abstract Private
     * Коллбэк для события по клику
     *
     */
    _onClick() {
        throw new Error(`Method _onClick() must be overridden`);
    }

    /**
     * @Private
     * Привязка методов к событиям открытия попапа
     *
     */
    _bindOpenEvents() {
        this.openBtns.forEach((button, index) => {
            button.addEventListener('click', (event) => {
                event.preventDefault();

                if (this.currentState) {
                    return;
                }

                this.currentTarget = button;

                this._open();
                this._onClick(index);
            });
        });
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа
     *
     */
    _bindCloseEvents() {
        this._closeByMouse();
        document.addEventListener('keyup', this._closeByKeyboard);
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа ко клику мышью
     *
     */
    _closeByMouse() {
        this.closeBtns.forEach((closeBtn) => {
            closeBtn.addEventListener('click', this.removePopup);
        });
    }

    /**
     * @Private
     * Привязка методов к событиям закрытия попапа по вводу с клавиатуры
     * @param {number} key - числовой код нажатой клавиши
     */
    _closeByKeyboard(key) {
        const keyCodeESC = 27;

        if (key.keyCode === keyCodeESC) {
            this.removePopup();
        }
    }

    /**
     * @Private
     * Получение необходимого дата-атрибута с кнопок открытия попапа
     * @param {string} attribute - атрибут, по которому происходит поиск
     * @return {String} - значение дата-атрибута
     *
     */
    _getDataAttrValue(attribute) {
        const attributesArray = [...new Set(this.openBtns.map((x) => {
            return x.getAttribute(attribute);
        }))];

        if (attributesArray.length !== 1 || !attributesArray[0]) {
            throw new Error(`Expect one ${attribute}. You set: ${attributesArray[0] ? attributesArray : 0}`);
        }

        return attributesArray[0];
    }
}

export class StaticPopup extends AbstractPopup {
    constructor(options) {
        super(options);

        /* @Override */
        this.closeStateClass = options.closeStateClass || 'popup_state_close';
        this.closeButtonAriaLabel = 'Закрыть';
        this.modify = options.modify || '';
        this.removeDelay = options.removeDelay || 0;
    }


    /* @Override */
    _setData() {
        const contentLink = this._getDataAttrValue('data-href');

        this.data = {
            content        : document.getElementById(contentLink).innerHTML,
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.modify
        };
    }

    /* @Override */
    _open() {
        this._createPopup();
        this.displayPopup();
    }

    /* @Override */
    // eslint-disable-next-line
    _onClick() {}

    /* @Override */
    removePopup() {
        if (this.removeDelay) {
            this.popup.classList.remove(this.openStateClass);
            this.popup.classList.add(this.closeStateClass);

            setTimeout(() => {
                super.removePopup();
            }, this.removeDelay);
        } else {
            super.removePopup();
        }
    }
}

export class StaticPopupWithHash extends StaticPopup {
    /* @Extend */
    _bindOpenEvents() {
        super._bindOpenEvents();

        this._parseUrl();
    }

    /* @Extend */
    displayPopup() {
        super.displayPopup();

        this._setHash();
    }

    /* @Extend */
    removePopup() {
        super.removePopup();

        this._clearHash();
    }

    /**
     * @Private
     * Открывает попап по хэшу в омнибоксе
     */
    _parseUrl() {
        const contentLink = this._getDataAttrValue('data-href');

        if (this.currentState || window.location.hash !== `#${contentLink}`) {
            return;
        }

        this._createPopup();
        this.displayPopup();
    }

    /**
     * @Private
     * Устанавливает хэш попапа в омнибокс браузера
     */
    _setHash() {
        const contentLink = this._getDataAttrValue('data-href');

        window.history.pushState(null, null, `#${contentLink}`);
    }

    /**
     * @Private
     * Очищает омнибокс браузера от хэша попапа
     */
    _clearHash() {
        if (window.history.pushState) {
            window.history.pushState(null, null, window.location.pathname + window.location.search);

            return;
        }

        window.location.hash = '';
    }
}

export class StaticPopupWithImage extends StaticPopup {
    _setTemplates() {
        this.template = imageTemplate;
    }

    /* @Override */
    _setData() {
        this.data = {
            buttonAriaLabel: this.closeButtonAriaLabel,
            modify         : this.modify,
            src            : this.openBtns[0].getAttribute('data-image'),
            caption        : this.openBtns[0].getAttribute('data-caption'),
            alt            : this.openBtns[0].getAttribute('data-alt')
        };
    }

    _onClick() {
        this._initPinch();
    }

    _initPinch() {
        if (!Utils.isTouch) {
            return;
        }

        const images = Array.from(this.popup.querySelectorAll('img'));

        images.forEach((item) => {
            new TouchZoom(item); // eslint-disable-line
        });
    }
}

export class AjaxPopup extends AbstractPopup {
    constructor(options) {
        super(options);

        /* Определяется в методе _sendDataRequest() */
        this.content = null;

        /* Определяется в методе _resending() */
        this.setInterval = null;

        this._setData = this._setData.bind(this);
        this.serverConnection = new InternetConnection(this._sendDataRequest.bind(this));
    }

    /**
     * @Private
     * Проверка на наличие интернета.
     * @return {String} Метод запроса из атрибута или POST, если атрибут пуст.
     */
    _getQueryType() {
        const attributesArray = [...new Set(this.openBtns.map((x) => {
            return x.getAttribute('data-query');
        }))];

        if (attributesArray.length > 1) {
            throw new Error(`Expect maximum one data-query. You set: ${attributesArray}`);
        }

        return attributesArray[0] || 'POST';
    }

    /**
     * @Private
     * Отправляет запрос на сервер и вызывает создание попапа с данными из ответа сервера
     */
    _sendDataRequest() {
        const that = this;
        const contentLink = this._getDataAttrValue('data-ajax');
        const callback = {
            success(req) {
                that.content = req.data.content;
            },
            error(err) {
                console.error(`Server error with status ${err}`);
            },
            complete() {
                that.content = that.content || that.serverConnection.getErrorMessage();

                that._createPopup();
                that.displayPopup();

                that.content = null;
            }
        };

        Utils.send(null, contentLink, callback, this._getQueryType());
    }

    /* @Override */
    _open() {
        this.serverConnection.connect();
    }

    /* @Override */
    _setData() {
        this.data = {
            content        : this.content,
            buttonAriaLabel: this.closeButtonAriaLabel
        };
    }
}

export class StaticResizePopup extends StaticPopup {
    constructor(options) {
        super(options);

        /* @Override */
        this.closeBreak = options.closeBreak || MOBILE_BREAKPOINT;
        this.closeOnResize = options.closeOnResize || 'mobile';
        this.view = Utils.getWindowWidth() < this.closeBreak ? 'mobile' : 'desktop';
        this._changeDevice = this._changeDevice.bind(this);
    }

    _bindCloseEvents() {
        super._bindCloseEvents();
        this._bindResize();
    }

    _bindResize() {
        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.addEventListener(event, this._changeDevice);
        });
    }

    _changeDevice() {
        if (!(Utils.getWindowWidth() < this.closeBreak) && this.view === 'mobile') {
            if (this.closeOnResize === 'mobile') {
                this.removePopup();
            }

            this.view = 'desktop';

            return;
        }

        if (Utils.getWindowWidth() < this.closeBreak && this.view === 'desktop') {
            if (this.closeOnResize === 'desktop') {
                this.removePopup();
            }

            this.view = 'mobile';
        }
    }

    /* @Override */
    removePopup() {
        super.removePopup();

        const events = ['resize', 'orientationchange'];

        events.forEach((event) => {
            window.removeEventListener(event, this._changeDevice);
        });
    }
}

export class FullPopup extends StaticPopup {
    constructor(options) {
        super(options);

        this.name = options.name;
    }

    /* @Override */
    _setTemplates() {
        this.template = fullTemplate;
    }

    /* @Override */
    _setData() {
        const contentLink = this._getDataAttrValue('data-full');

        this.data = {
            content        : document.getElementById(contentLink).innerHTML,
            modify         : this.modify,
            buttonAriaLabel: this.closeButtonAriaLabel,
            coords         : this.currentTarget.dataset.coords
        };
    }
}

export class MobileGalleryPopup extends StaticResizePopup {
    constructor(options) {
        super(options);

        /* @Override */
        this.closeButtonAriaLabel = 'Закрыть';
        this.sliderSelector = '.j-popup-slider';
    }

    /* @Override */
    _setTemplates() {
        this.template = sliderTemplate;
    }

    /* @Override */
    _setData() {
        this.data = {
            modify         : this.modify,
            buttonAriaLabel: this.closeButtonAriaLabel,
            items          : this._collectSlidesData()
        };
    }

    _collectSlidesData() {
        return this.openBtns.map((item) => {
            return {
                src: item.getAttribute('data-src'),
                alt: item.getAttribute('data-alt')
            };
        });
    }

    /* @Override */
    _onClick(index) {
        // super._onClick();
        this.initialSlide = index;
        this._initSlider();
        this.onClose = this._destroySlider;
    }

    _initSlider() {
        this.slider = $(this.popup.querySelector(this.sliderSelector)).slick({
            arrows       : false,
            infinite     : false,
            initialSlide : this.initialSlide,
            focusOnSelect: true
        });

        this._initSliderSubscribes();
        this._initPinch();
    }

    _initSliderSubscribes() {
        observer.subscribe('isPinchEnd', (scale) => {
            const isDraggable = !(this.slider.slick('slickGetOption', 'draggable') && scale > 1);

            this.slider.slick('slickSetOption', 'draggable', isDraggable, isDraggable);
            this.slider.slick('slickSetOption', 'swipe', isDraggable, isDraggable);
        });
    }

    _initPinch() {
        const images = Array.from(this.slider[0].querySelectorAll('img'));

        images.forEach((item) => {
            new TouchZoom(item); // eslint-disable-line
        });
    }

    _destroySlider() {
        this.slider.slick('unslick');
    }
}
